import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { apiService, RankingTimelineResponse, RankingTimelineSnapshot } from '../services/api';
import { format, parseISO } from 'date-fns';

interface ProductRankingTimelineProps {
  sku: string;
  defaultRangeHours?: number;
}

type RangeOption = 12 | 24 | 48 | 168;

interface ChartDataPoint {
  timestamp: string;
  formattedTime: string;
  rank_atual: number | null;
  penalty_v2_factor: number | null;
}

const ProductRankingTimeline: React.FC<ProductRankingTimelineProps> = ({
  sku,
  defaultRangeHours = 24
}) => {
  const [data, setData] = useState<RankingTimelineResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [rangeHours, setRangeHours] = useState<RangeOption>(defaultRangeHours as RangeOption);
  const [visibleLines, setVisibleLines] = useState<{[key: string]: boolean}>({
    rank_atual: true,
    penalty_v2_factor: true
  });

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getProductRankingTimeline(sku, rangeHours);
        setData(response);
      } catch (err: any) {
        console.error('Error fetching ranking timeline:', err);
        setError(err.response?.data?.error || 'Erro ao carregar timeline de ranking');
      } finally {
        setLoading(false);
      }
    };

    if (sku) {
      fetchTimeline();
    }
  }, [sku, rangeHours]);

  const toggleLine = (lineKey: string) => {
    setVisibleLines(prev => ({
      ...prev,
      [lineKey]: !prev[lineKey]
    }));
  };

  const formatChartData = (snapshots: RankingTimelineSnapshot[]): ChartDataPoint[] => {
    return snapshots.map(snapshot => ({
      timestamp: snapshot.timestamp || '',
      formattedTime: snapshot.timestamp ? format(parseISO(snapshot.timestamp), 'dd/MM HH:mm') : '',
      rank_atual: snapshot.rank_atual,
      penalty_v2_factor: snapshot.penalty_v2_factor ? snapshot.penalty_v2_factor * 100 : null // Convert to percentage
    }));
  };

  // Custom Tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="card shadow-sm" style={{ maxWidth: '300px' }}>
          <div className="card-body p-2">
            <p className="mb-2 fw-bold small">{label}</p>
            {payload.map((entry: any, index: number) => (
              <div key={index} className="d-flex justify-content-between small" style={{ color: entry.color }}>
                <span>{entry.name}:</span>
                <span className="ms-2 fw-bold">
                  {entry.dataKey === 'rank_atual' && '#'}
                  {entry.value !== null ? entry.value.toFixed(entry.dataKey === 'penalty_v2_factor' ? 1 : 0) : 'N/A'}
                  {entry.dataKey === 'penalty_v2_factor' && '%'}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="card shadow-sm">
        <div className="card-body text-center py-5">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">A carregar...</span>
          </div>
          <p className="text-muted mt-2 mb-0 small">A carregar timeline de ranking...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card shadow-sm border-warning">
        <div className="card-body">
          <div className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill text-warning me-2 fs-5"></i>
            <div>
              <h6 className="mb-1">Ranking Evolution</h6>
              <p className="text-muted mb-0 small">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.snapshots.length === 0) {
    return (
      <div className="card shadow-sm">
        <div className="card-body text-center py-4">
          <i className="bi bi-inbox text-muted fs-1"></i>
          <p className="text-muted mt-2 mb-0">Sem dados de ranking disponíveis para este período</p>
        </div>
      </div>
    );
  }

  const chartData = formatChartData(data.snapshots);

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-light">
        <div className="row align-items-center">
          <div className="col">
            <h6 className="mb-0">
              <i className="bi bi-graph-up me-2"></i>
              Ranking Evolution - Last {rangeHours}h
            </h6>
          </div>
          <div className="col-auto">
            <div className="btn-group btn-group-sm" role="group">
              {[12, 24, 48, 168].map((hours) => (
                <button
                  key={hours}
                  type="button"
                  className={`btn ${rangeHours === hours ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setRangeHours(hours as RangeOption)}
                >
                  {hours === 168 ? '7d' : `${hours}h`}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="card-body">
        {/* Chart */}
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="formattedTime"
              angle={-45}
              textAnchor="end"
              height={80}
              tick={{ fontSize: 11 }}
              stroke="#666"
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              reversed={true}  // Inverted for ranking position (lower is better)
              label={{ value: 'Posição no Ranking', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
              tick={{ fontSize: 11 }}
              stroke="#666"
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              label={{ value: 'Penalty %', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
              tick={{ fontSize: 11 }}
              stroke="#666"
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              onClick={(e) => toggleLine(e.dataKey as string)}
              wrapperStyle={{ cursor: 'pointer', fontSize: '12px' }}
            />

            {/* Rank Atual (Inverted Y-axis) */}
            {visibleLines.rank_atual && (
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="rank_atual"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={{ r: 3 }}
                name="Rank Atual"
                connectNulls
                opacity={visibleLines.rank_atual ? 1 : 0.2}
              />
            )}

            {/* Penalty V2.0 Factor (as percentage) */}
            {visibleLines.penalty_v2_factor && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="penalty_v2_factor"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{ r: 3 }}
                name="Penalty V2.0 (%)"
                connectNulls
                opacity={visibleLines.penalty_v2_factor ? 1 : 0.2}
              />
            )}
          </LineChart>
        </ResponsiveContainer>

        {/* Legend Helper */}
        <div className="mt-3 p-2 bg-light rounded small text-muted">
          <i className="bi bi-info-circle me-2"></i>
          <strong>Dica:</strong> Clique numa legenda para mostrar/ocultar a série no gráfico
        </div>

        {/* Summary Stats */}
        <div className="row g-2 mt-3">
          <div className="col-3">
            <div className="text-center p-2 bg-light rounded">
              <div className="text-muted small">Snapshots</div>
              <div className="fw-bold">{data.count}</div>
            </div>
          </div>
          <div className="col-4">
            <div className="text-center p-2 bg-light rounded">
              <div className="text-muted small">Rank Atual</div>
              <div className="fw-bold">
                #{chartData[chartData.length - 1]?.rank_atual || 'N/A'}
              </div>
            </div>
          </div>
          <div className="col-4">
            <div className="text-center p-2 bg-light rounded">
              <div className="text-muted small">Penalty Atual</div>
              <div className="fw-bold">
                {chartData[chartData.length - 1]?.penalty_v2_factor?.toFixed(1) || 'N/A'}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductRankingTimeline;
